import React, { useState, useEffect } from 'react'
import { quotationService, projectService, purchaseOrderService } from '../services/api'
import TableStateWrapper from '../components/TableStateWrapper'
import TablePagination from '../components/TablePagination'
import useResponsivePageSize from '../hooks/useResponsivePageSize'
import { formatCurrency } from '../utils/currency'
import { formatDate, formatDateForInput } from '../utils/dateFormat'
import GeneratedPdfPreviewModal from '../components/GeneratedPdfPreviewModal'
import TemplateDocumentPreviewModal from '../components/TemplateDocumentPreviewModal'
import useRolePermissions from '../hooks/useRolePermissions'
import { exportTemplatePreviewPdf } from '../utils/exportTemplatePreviewPdf'
import MultiSelectFilterDropdown from '../components/MultiSelectFilterDropdown'
import TruncatedText from '../components/TruncatedText'
import CollapsibleFilters from '../components/CollapsibleFilters'
import { getBaseUrl } from '../utils/apiConfig'

const API_BASE_URL = getBaseUrl()

export default function Quotations() {
  const { isViewer, canMutate } = useRolePermissions()
  const [quotations, setQuotations] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(false)
  const [tableError, setTableError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [editingItemId, setEditingItemId] = useState(null)
  const [items, setItems] = useState([])
  const [originalItems, setOriginalItems] = useState([])
  const [currentItem, setCurrentItem] = useState({
    description: '',
    price: '',
    quantity: ''
  })
  const [quotationItemsPage, setQuotationItemsPage] = useState(1)
  const { containerRef: quotationItemsTableRef, rowsPerPage: quotationItemsRowsPerPage } = useResponsivePageSize({ reservedHeight: 640, footerHeight: 24, rowHeight: 48, minRows: 3, maxRows: 6 })
  const [viewingPdfPath, setViewingPdfPath] = useState(null)
  const [viewingFileInModal, setViewingFileInModal] = useState(null)
  const [viewerDownloadFile, setViewerDownloadFile] = useState(null)
  const [templatePreviewData, setTemplatePreviewData] = useState(null)
  const [pendingTemplateDownload, setPendingTemplateDownload] = useState(null)
  const [messageModal, setMessageModal] = useState({ open: false, title: 'Notification', message: '' })
  const [confirmModal, setConfirmModal] = useState({ open: false, title: 'Confirm', message: '', resolver: null })
  const [selectedProject, setSelectedProject] = useState(null)
  const [selectedQuotation, setSelectedQuotation] = useState(null)
  const [isFieldsEditable, setIsFieldsEditable] = useState(false)
  const [formData, setFormData] = useState({
    project_id: '',
    quotation_number: '',
    quotation_date: new Date().toISOString().split('T')[0],
    quotation_amount: '',
    status: 'Proposal',
    remarks: '',
    recipient_name: '',
    remarks_type: '',
    payment_term: '',
    po_no: '',
    po_attachment: null,
    po_attachment_name: ''
  })
  const [filters, setFilters] = useState({
    project_ids: [],
    statuses: [],
    clientNames: [],
    quotationNumbers: []
  })
  const [quotationPage, setQuotationPage] = useState(1)
  const { containerRef: quotationTableRef, rowsPerPage: quotationRowsPerPage } = useResponsivePageSize({ reservedHeight: 480, footerHeight: 24, rowHeight: 52, minRows: 5, maxRows: 14 })

  useEffect(() => {
    fetchQuotations()
    fetchProjects()
    
    // Check if coming from Projects page with project_id
    const params = new URLSearchParams(window.location.search)
    const projectId = params.get('project_id')
    if (projectId) {
      const project = projects.find(p => p.project_id === parseInt(projectId))
      if (project) {
        setSelectedProject(project)
        setFormData(prev => ({ ...prev, project_id: projectId }))
        setShowModal(true)
        // Clear the URL parameter
        window.history.replaceState({}, document.title, window.location.pathname)
      }
    }
  }, [])

  useEffect(() => {
    const handleFocus = () => {
      fetchQuotations()
      fetchProjects()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  useEffect(() => {
    const hasPendingOutgoingQuotations = quotations.some((quotation) => {
      const quotationType = normalizeTypeLabel(quotation?.remarks_type || quotation?.remarksType || quotation?.qt_type || quotation?.qtType || quotation?.quotation_type)
      const pdfStatus = String(quotation?.pdf_generation_status || 'ready').toLowerCase()
      return quotationType === 'Outgoing' && pdfStatus === 'pending'
    })

    if (!hasPendingOutgoingQuotations) {
      return undefined
    }

    const timer = setInterval(async () => {
      try {
        const response = await quotationService.getAll()
        setQuotations(response.data?.data || [])
      } catch (error) {
        // PDF refresh failed silently
      }
    }, 5000)

    return () => clearInterval(timer)
  }, [quotations])

  useEffect(() => {
    if (!pendingTemplateDownload?.id) return

    const quotation = quotations.find((item) => item.quotation_id === pendingTemplateDownload.id)
    if (!quotation) return

    const pdfStatus = String(quotation.pdf_generation_status || 'ready').toLowerCase()

    if (pdfStatus === 'failed') {
      setPendingTemplateDownload(null)
      showMessage(quotation.pdf_generation_error || 'PDF generation failed', 'Error')
      return
    }

    if (pdfStatus === 'ready' && quotation.pdf_path) {
      downloadFile(quotation.pdf_path, pendingTemplateDownload.fileName || `${quotation.quotation_number || 'quotation'}.pdf`)
      setPendingTemplateDownload(null)
    }
  }, [quotations, pendingTemplateDownload])

  const fetchQuotations = async () => {
    try {
      setLoading(true)
      setTableError(null)
      const response = await quotationService.getAll()
      setQuotations(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch quotations:', error)
      setTableError(error.response?.data?.message || 'Failed to fetch quotations')
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    try {
      const response = await projectService.getAll()
      setProjects(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch projects:', error)
    }
  }

  const generateQuotationNumber = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const random = Math.floor(Math.random() * 10000).toString().padStart(5, '0')
    return `QT-${year}-${month}-${random}`
  }

  const showMessage = (message, title = 'Notification') => {
    setMessageModal({ open: true, title, message: String(message || '') })
  }

  const showConfirm = (message, title = 'Confirm') => {
    return new Promise((resolve) => {
      setConfirmModal({
        open: true,
        title,
        message: String(message || ''),
        resolver: resolve
      })
    })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    if (name === 'project_id') {
      const project = projects.find(p => p.project_id === parseInt(value))
      setSelectedProject(project || null)
    }
  }

  const showOutgoingInfo = () => {
    showMessage('This + New button creates standalone Incoming documents only. It does not trigger project-flow actions.', 'Information')
  }

  const handleUpdateQuotation = async () => {
    try {
      setLoading(true)
      const linkedProject = projects.find(p => p.project_id === selectedQuotation.project_id)
      
      // Check if quotation status has changed
      const statusChanged = selectedQuotation.status !== formData.status
      const isStatusChangingToConfirm = statusChanged && formData.status === 'Confirm' && selectedQuotation.status === 'Proposal'

      if (formData.status === 'Confirm' && !formData.po_no?.trim()) {
        showMessage('PO No. is required when status is Confirm', 'Validation')
        setLoading(false)
        return
      }

      if (formData.status === 'Confirm' && !formData.po_attachment && !formData.po_attachment_name) {
        showMessage('PO attachment is required when status is Confirm', 'Validation')
        setLoading(false)
        return
      }

      let attachmentPath = formData.po_attachment_name || linkedProject?.po_attachment || null

      if (formData.status === 'Confirm') {
        const poDate = new Date().toISOString().split('T')[0]

        const existingPOs = await purchaseOrderService.getByProject(selectedQuotation.project_id)
        const existingPO = existingPOs.data.data && existingPOs.data.data.length > 0 ? existingPOs.data.data[0] : null

        if (existingPO) {
          // PO document uploads were removed — the generated PDF lives in pdf_path.
          await purchaseOrderService.update(existingPO.po_id, {
            po_number: formData.po_no,
            po_date: poDate,
            po_value: parseFloat(formData.quotation_amount || 0),
            vendor_client_details: selectedQuotation.company_name || linkedProject?.company_name || '',
            remarks: formData.remarks || '',
            remarks_type: formData.remarks_type || null,
            payment_term: formData.payment_term || null,
            status: 'Unpaid'
          })
        } else {
          await purchaseOrderService.create({
            project_id: selectedQuotation.project_id,
            po_number: formData.po_no,
            po_date: poDate,
            po_value: parseFloat(formData.quotation_amount || 0),
            vendor_client_details: selectedQuotation.company_name || linkedProject?.company_name || '',
            remarks: formData.remarks || '',
            remarks_type: formData.remarks_type || null,
            payment_term: formData.payment_term || null,
            status: 'Unpaid',
            detail_items: []
          })
        }
      }
      
      // Update quotation main data
      await quotationService.update(selectedQuotation.quotation_id, {
        amount: parseFloat(formData.quotation_amount),
        quotation_date: formData.quotation_date,
        status: formData.status,
        remarks: formData.remarks,
        recipient_name: formData.recipient_name || null,
        remarks_type: formData.remarks_type || null,
        payment_term: formData.payment_term || null,
        po_no: formData.po_no || null,
        po_attachment: attachmentPath || null
      })
      
      if (linkedProject) {
        const projectStatus = formData.status === 'Confirm' ? 'Confirm' : (formData.status === 'Close' ? 'Close' : linkedProject.status)
        await projectService.update(selectedQuotation.project_id, {
          status: projectStatus,
          po_no: formData.po_no || linkedProject.po_no || null,
          po_attachment: attachmentPath || linkedProject.po_attachment || null
        })
      }
      
      // Update detail items in the project
      // 1. Delete removed items (items that were in originalItems but not in items)
      const removedItems = originalItems.filter(origItem => 
        !items.find(newItem => newItem.id === origItem.id)
      )
      
      for (const removedItem of removedItems) {
        if (removedItem.detail_item_id) {
          try {
            await projectService.deleteDetailItem(selectedQuotation.project_id, removedItem.detail_item_id)
          } catch (error) {
            // Could not delete detail item
          }
        }
      }
      
      // 2. Update existing items and add new items to project
      for (const item of items) {
        if (item.detail_item_id) {
          // Update existing item in project
          try {
            await projectService.updateDetailItem(selectedQuotation.project_id, item.detail_item_id, {
              description: item.description,
              price: parseFloat(item.price),
              quantity: parseFloat(item.quantity),
              amount: parseFloat(item.amount)
            })
          } catch (error) {
            console.error('Error updating detail item:', error)
          }
        } else {
          // Add new item to project
          try {
            const response = await projectService.addDetailItem(selectedQuotation.project_id, {
              description: item.description,
              price: parseFloat(item.price),
              quantity: parseFloat(item.quantity),
              amount: parseFloat(item.amount)
            })
            // Update the item with the returned detail_item_id
            if (response.data && response.data.data && response.data.data.detail_item_id) {
              item.detail_item_id = response.data.data.detail_item_id
            }
          } catch (error) {
            console.error('Error adding detail item:', error)
          }
        }
      }
      
      let successMessage = '✓ Quotation updated successfully!'
      if (isStatusChangingToConfirm) {
        successMessage += '\n✓ Project status also updated to Confirm!'
      }
      showMessage(successMessage, 'Success')
      await fetchQuotations()
      await fetchProjects()
      closeDetailsModal()
    } catch (error) {
      console.error('Error updating quotation:', error)
      showMessage('Failed to update quotation: ' + (error.response?.data?.message || error.message), 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateQuotation = async () => {
    try {
      // Validate project selection
      if (!formData.project_id) {
        showMessage('Please select a project before creating quotation', 'Validation')
        return
      }

      if (items.length === 0) {
        showMessage('Please add at least one detail item before creating quotation', 'Validation')
        return
      }

      setLoading(true)

      // Sync create detail items to project so generated quotation PDF includes all latest rows.
      const existingItemsResponse = await projectService.getDetailItems(parseInt(formData.project_id))
      const existingItems = existingItemsResponse.data && existingItemsResponse.data.data
        ? existingItemsResponse.data.data
        : []

      for (const existingItem of existingItems) {
        if (existingItem.detail_item_id) {
          await projectService.deleteDetailItem(parseInt(formData.project_id), existingItem.detail_item_id)
        }
      }

      for (const item of items) {
        await projectService.addDetailItem(parseInt(formData.project_id), {
          description: item.description,
          price: parseFloat(item.price),
          quantity: parseFloat(item.quantity),
          amount: parseFloat(item.amount)
        })
      }

      const totalAmount = items.reduce((sum, item) => sum + Number(item.amount || 0), 0)

      const quotationNumber = generateQuotationNumber()
      const quotationDate = formData.quotation_date || new Date().toISOString().split('T')[0]

      if (formData.po_attachment) {
        const createFormData = new FormData()
        createFormData.append('project_id', String(formData.project_id))
        createFormData.append('quotation_number', quotationNumber)
        createFormData.append('quotation_date', quotationDate)
        createFormData.append('quotation_amount', totalAmount.toFixed(2))
        createFormData.append('status', 'Proposal')
        createFormData.append('remarks', formData.remarks || '')
        createFormData.append('recipient_name', formData.recipient_name || '')
        createFormData.append('remarks_type', 'Incoming')
        createFormData.append('payment_term', formData.payment_term || '')
        createFormData.append('document', formData.po_attachment)

        await quotationService.createWithFile(createFormData)
      } else {
        const quotationData = {
          ...formData,
          quotation_number: quotationNumber,
          quotation_date: quotationDate,
          quotation_amount: totalAmount.toFixed(2),
          recipient_name: formData.recipient_name || null,
          remarks_type: 'Incoming'
        }

        await quotationService.create(quotationData)
      }

      showMessage(`Quotation ${quotationNumber} created successfully!`, 'Success')
      setShowModal(false)
      setFormData({
        project_id: '',
        quotation_number: '',
        quotation_date: new Date().toISOString().split('T')[0],
        quotation_amount: '',
        status: 'Proposal',
        remarks: '',
        remarks_type: 'Incoming',
        po_no: '',
        po_attachment: null,
        po_attachment_name: ''
      })
      setItems([])
      setCurrentItem({ description: '', price: '', quantity: '' })
      setSelectedProject(null)
      await fetchQuotations()
      await fetchProjects()
    } catch (error) {
      console.error('Failed to create quotation:', error)
      showMessage(error.response?.data?.message || 'Failed to create quotation', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteQuotation = async (quotationId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this quotation?')
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      await quotationService.delete(quotationId)
      showMessage('Quotation deleted successfully!', 'Success')
      await fetchQuotations()
    } catch (error) {
      console.error('Failed to delete quotation:', error)
      showMessage(error.response?.data?.message || 'Failed to delete quotation', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const generateLineItemId = () => Math.random().toString(36).substr(2, 9)

  const handleViewDetails = async (quotation) => {
    try {
      setSelectedQuotation(quotation)
      const linkedProject = projects.find(project => project.project_id === quotation.project_id)
      const effectiveStatus = linkedProject?.status || quotation.status || 'Proposal'
      // Format the date properly for the input field (yyyy-MM-dd format)
      const dateString = quotation.quotation_date 
        ? new Date(quotation.quotation_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]
      
      setFormData({
        project_id: quotation.project_id,
        quotation_number: quotation.quotation_number,
        quotation_date: dateString,
        quotation_amount: quotation.amount,
        status: effectiveStatus,
        remarks: quotation.remarks || '',
        payment_term: quotation.payment_term || '',
        recipient_name: quotation.recipient_name || '',
        remarks_type: quotation.remarks_type || '',
        po_no: linkedProject?.po_no || quotation.po_no || '',
        po_attachment: null,
        po_attachment_name: linkedProject?.po_attachment || quotation.po_attachment || ''
      })
      
      // Determine if fields should be editable
      // Fields are editable only if quotation status is "Proposal"
      const canEdit = !isViewer && effectiveStatus === 'Proposal'
      setIsFieldsEditable(canEdit)
      
      // Load detail items from project (detail items are stored with projects, not quotations)
      let projectDetailItems = []
      try {
        const projectResponse = await projectService.getDetailItems(quotation.project_id)
        projectDetailItems = projectResponse.data && projectResponse.data.data 
          ? projectResponse.data.data 
          : (Array.isArray(projectResponse.data) ? projectResponse.data : [])
        
        if (projectDetailItems.length > 0) {
          // Load project detail items as quotation items
          const loadedItems = projectDetailItems.map(item => ({
            id: generateLineItemId(),
            detail_item_id: item.detail_item_id,
            description: item.description,
            price: parseFloat(item.price),
            quantity: parseFloat(item.quantity),
            amount: parseFloat(item.amount || item.price * item.quantity)
          }))
          setItems(loadedItems)
          setOriginalItems(loadedItems) // Track original items
          
          // Auto-calculate total amount from project items
          const total = projectDetailItems.reduce((sum, item) => sum + parseFloat(item.amount || item.price * item.quantity), 0)
          setFormData(prev => ({ ...prev, quotation_amount: total.toFixed(2) }))
        }
      } catch (error) {
        // Could not load project detail items
      }
      
      if (!projectDetailItems || projectDetailItems.length === 0) {
        setItems([])
        setOriginalItems([])
      }
      
      setCurrentItem({ description: '', price: '', quantity: '' })
      setEditingItemId(null)
      setShowDetailsModal(true)
    } catch (error) {
      console.error('Error loading quotation details:', error)
    }
  }

  const handleItemChange = (e) => {
    const { name, value } = e.target
    setCurrentItem(prev => ({ ...prev, [name]: value }))
  }

  const addItem = () => {
    if (!currentItem.description || !currentItem.price || !currentItem.quantity) {
      showMessage('Please fill in all item fields', 'Validation')
      return
    }
    const amount = parseFloat(currentItem.price) * parseFloat(currentItem.quantity)
    const newItems = [
      ...items,
      {
        id: generateLineItemId(),
        description: currentItem.description,
        price: parseFloat(currentItem.price),
        quantity: parseFloat(currentItem.quantity),
        amount: amount
      }
    ]
    setItems(newItems)
    setCurrentItem({ description: '', price: '', quantity: '' })
    
    // Auto-update amount field with total
    const total = newItems.reduce((sum, item) => sum + item.amount, 0)
    setFormData(prev => ({ ...prev, quotation_amount: total.toFixed(2) }))
  }

  const removeItem = async (id) => {
    try {
      // If item has quotation_detail_id, delete from backend first
      const itemToDelete = items.find(item => item.id === id)
      if (itemToDelete && itemToDelete.quotation_detail_id) {
        await quotationService.deleteDetailItem(selectedQuotation.quotation_id, itemToDelete.quotation_detail_id)
      }
      
      const newItems = items.filter(item => item.id !== id)
      setItems(newItems)
      
      // Auto-update amount field with new total
      const total = newItems.reduce((sum, item) => sum + item.amount, 0)
      setFormData(prev => ({ ...prev, quotation_amount: total.toFixed(2) }))
    } catch (error) {
      console.error('Error deleting item:', error)
      showMessage('Failed to delete item', 'Error')
    }
  }

  const getTotalAmount = () => {
    return items.reduce((total, item) => total + item.amount, 0)
  }

  const startEditItem = (item) => {
    setEditingItemId(item.id)
    setCurrentItem({
      description: item.description,
      price: item.price,
      quantity: item.quantity
    })
  }

  const saveEditItem = (id) => {
    if (!currentItem.description || !currentItem.price || !currentItem.quantity) {
      showMessage('Please fill in all item fields', 'Validation')
      return
    }
    const amount = parseFloat(currentItem.price) * parseFloat(currentItem.quantity)
    const newItems = items.map(item =>
      item.id === id
        ? {
            ...item,
            description: currentItem.description,
            price: parseFloat(currentItem.price),
            quantity: parseFloat(currentItem.quantity),
            amount: amount
          }
        : item
    )
    setItems(newItems)
    setEditingItemId(null)
    setCurrentItem({ description: '', price: '', quantity: '' })
    
    // Auto-update amount field with new total
    const total = newItems.reduce((sum, item) => sum + item.amount, 0)
    setFormData(prev => ({ ...prev, quotation_amount: total.toFixed(2) }))
  }

  const downloadFile = async (filePath, fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}${filePath}`)
      if (!response.ok) throw new Error('Download failed')
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || filePath.split('/').pop() || 'file'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      showMessage('Failed to download file', 'Error')
    }
  }

  const handleDownloadPreviewPdf = () => {
    if (!viewingPdfPath) return
    const pathWithoutBase = viewingPdfPath.replace(API_BASE_URL, '')
    const fallbackName = `${selectedQuotation?.quotation_number || 'quotation'}.pdf`
    downloadFile(pathWithoutBase, fallbackName)
  }

  const handleDownloadTemplatePreviewPdf = async (previewElement, fileName) => {
    try {
      await exportTemplatePreviewPdf(previewElement, fileName || `${templatePreviewData?.number || 'quotation'}.pdf`)
    } catch (error) {
      showMessage(error.message || 'Failed to download template preview PDF', 'Error')
      throw error
    }
  }

  const handleViewPDF = (pdfPath) => {
    if (!pdfPath) {
      showMessage('PDF not available for this quotation', 'Information')
      return
    }
    setViewingPdfPath(`${API_BASE_URL}${pdfPath}`)
  }

  const toNumber = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const openQuotationTemplatePreview = async (quotation) => {
    try {
      const detailResponse = await projectService.getDetailItems(quotation.project_id)
      const detailItems = detailResponse.data && detailResponse.data.data
        ? detailResponse.data.data
        : (Array.isArray(detailResponse.data) ? detailResponse.data : [])

      let previewItems = detailItems.map((item, index) => {
        const price = toNumber(item.price)
        const qty = toNumber(item.quantity)
        return {
          id: item.detail_item_id || `row-${index + 1}`,
          description: item.description || '',
          price,
          qty,
          amount: price * qty
        }
      })

      if (previewItems.length === 0) {
        const fallbackAmount = toNumber(quotation.amount)
        previewItems = [{
          id: 'fallback-1',
          description: quotation.remarks || 'Works as per quotation',
          price: fallbackAmount,
          qty: 1,
          amount: fallbackAmount
        }]
      }

      const totalAmount = previewItems.reduce((sum, item) => sum + toNumber(item.amount), 0)

      setTemplatePreviewData({
        id: quotation.quotation_id,
        number: quotation.quotation_number || 'quotation',
        fileName: `${quotation.quotation_number || 'quotation'}.pdf`,
        template: 'quotation',
        title: `Quotation Preview - ${quotation.quotation_number || 'N/A'}`,
        downloadPath: quotation.pdf_path || null,
        draft: {
          companyName: 'PROBARA GIGIH TRADING (003614861-V)',
          companyAddress1: '(IMG2), JALAN SALAK TINGGI',
          companyAddress2: 'KAMPUNG JENDERAM HULU, 43800 DENGKIL, SELANGOR',
          companyAddress3: 'Email : probaragigih@gmail.com',
          quotationNo: quotation.quotation_number || '-',
          recipientName: quotation.recipient_name || quotation.company_name || 'Recipient',
          poNo: quotation.po_no || '-',
          issuedDate: quotation.quotation_date ? new Date(quotation.quotation_date).toISOString().split('T')[0] : '-',
          referenceNo: quotation.remarks || '-',
          paymentTerm: quotation.payment_term || '-',
          projectName: quotation?.Project?.project_name || '-',
          quotationTerms: 'By proceeding with this PO, you agree to the terms and conditions outlined in this PO'
        },
        items: previewItems,
        totals: {
          totalAmount
        }
      })
    } catch (error) {
      console.error('Failed to load quotation template preview:', error)
      showMessage('Failed to open quotation preview', 'Error')
    }
  }

  const viewFileInModal = (filePath, fileName = null) => {
    const fullPath = `${API_BASE_URL}${filePath}`
    setViewerDownloadFile({ path: filePath, name: fileName || filePath.split('/').pop() || 'file' })
    if (getFileExtension(filePath) === 'pdf') {
      setViewingPdfPath(fullPath)
      return
    }
    setViewingFileInModal(fullPath)
  }

  const openUploadedFileViewer = (quotation) => {
    const uploadedPath = quotation?.po_attachment || quotation?.document_path || quotation?.uploaded_file_path
    if (!uploadedPath) {
      showMessage('Uploaded file not available for this quotation', 'Information')
      return
    }

    const uploadedName = uploadedPath.split('/').pop() || `${quotation.quotation_number || 'Quotation'}-uploaded`
    viewFileInModal(uploadedPath, uploadedName)
  }

  const closeFileViewer = () => {
    setViewingPdfPath(null)
    setViewingFileInModal(null)
    setViewerDownloadFile(null)
  }

  const getFileExtension = (filePath) => {
    return filePath?.split('.').pop()?.toLowerCase() || ''
  }

  const FileIconButton = ({ onClick, icon, tooltip, disabled = false }) => {
    const [showTooltip, setShowTooltip] = useState(false)

    return (
      <div className="relative inline-block">
        <button
          onClick={onClick}
          disabled={disabled}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="text-blue-600 hover:text-blue-800 text-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
          title={tooltip}
        >
          {icon}
        </button>
        {showTooltip && !disabled && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap pointer-events-none">
            {tooltip}
          </div>
        )}
      </div>
    )
  }

  const openCreateModal = () => {
    setFormData({
      project_id: '',
      quotation_number: '',
      quotation_date: new Date().toISOString().split('T')[0],
      quotation_amount: '',
      status: 'Proposal',
      remarks: '',
      payment_term: '',
      recipient_name: '',
      remarks_type: 'Incoming',
      po_no: '',
      po_attachment: null,
      po_attachment_name: ''
    })
    setItems([])
    setCurrentItem({ description: '', price: '', quantity: '' })
    setEditingItemId(null)
    setSelectedProject(null)
    setShowModal(true)
  }

  const closeDetailsModal = () => {
    setShowDetailsModal(false)
    setSelectedQuotation(null)
    setItems([])
    setOriginalItems([])
    setFormData({
      project_id: '',
      quotation_number: '',
      quotation_date: new Date().toISOString().split('T')[0],
      quotation_amount: '',
      status: 'Proposal',
      remarks: '',
      payment_term: '',
      recipient_name: '',
      remarks_type: '',
      po_no: '',
      po_attachment: null,
      po_attachment_name: ''
    })
  }

  const getFilteredQuotations = () => {
    return quotations.filter(q => {
      const projectId = String(q?.project_id || '')
      const quotationNumber = String(q?.quotation_number || '')
      const matchClientName = filters.clientNames.length === 0 || filters.clientNames.includes(q?.company_name || q?.Project?.company_name || '')
      const matchProject = filters.project_ids.length === 0 || filters.project_ids.includes(projectId)
      const matchStatus = filters.statuses.length === 0 || filters.statuses.includes(q?.status || '')
      const matchQuotationNumber = filters.quotationNumbers.length === 0 || filters.quotationNumbers.includes(quotationNumber)
      return matchProject && matchStatus && matchClientName && matchQuotationNumber
    })
  }

  const getUniqueClientNames = () => {
    return [...new Set(quotations.map((quotation) => String(quotation?.company_name || quotation?.Project?.company_name || '').trim()).filter(Boolean))].sort()
  }

  const getUniqueProjectOptions = () => {
    return projects.map((project) => ({
      value: String(project.project_id),
      label: `${project.project_code} - ${project.project_name}`
    }))
  }

  const getUniqueStatuses = () => {
    return ['Proposal', 'Confirm', 'Close']
  }

  const getUniqueQuotationNumbers = () => {
    return [...new Set(quotations.map((quotation) => String(quotation?.quotation_number || '').trim()).filter(Boolean))].sort()
  }

  const filteredQuotations = getFilteredQuotations()
  const totalQuotationPages = Math.max(1, Math.ceil(filteredQuotations.length / quotationRowsPerPage))
  const paginatedQuotations = filteredQuotations.slice((quotationPage - 1) * quotationRowsPerPage, quotationPage * quotationRowsPerPage)

  useEffect(() => {
    setQuotationPage(1)
  }, [filters, quotationRowsPerPage])

  useEffect(() => {
    setQuotationPage((prev) => Math.min(prev, totalQuotationPages))
  }, [totalQuotationPages])

  const totalQuotationItemPages = Math.max(1, Math.ceil(items.length / quotationItemsRowsPerPage))
  const paginatedQuotationItems = items.slice((quotationItemsPage - 1) * quotationItemsRowsPerPage, quotationItemsPage * quotationItemsRowsPerPage)

  useEffect(() => {
    setQuotationItemsPage((prev) => Math.min(prev, totalQuotationItemPages))
  }, [totalQuotationItemPages])

  const resetFilters = () => {
    setFilters({
      project_ids: [],
      statuses: [],
      clientNames: [],
      quotationNumbers: []
    })
  }

  const isConfirmWithoutAttachment =
    isFieldsEditable &&
    formData.status === 'Confirm' &&
    !formData.po_attachment &&
    !formData.po_attachment_name

  const normalizeTypeLabel = (rawType) => {
    const normalized = String(rawType || '').trim().toLowerCase()
    if (normalized === 'outgoing') return 'Outgoing'
    if (normalized === 'incoming') return 'Incoming'
    return ''
  }

  const getTypeBadge = (rawType) => {
    const typeLabel = normalizeTypeLabel(rawType)
    const typeColors = {
      'Outgoing': 'bg-amber-100 text-amber-800',
      'Incoming': 'bg-sky-100 text-sky-800'
    }
    return {
      label: typeLabel || 'N/A',
      className: typeColors[typeLabel] || 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">Quotations</h1>
        <div className="flex flex-wrap items-center gap-2">
          {canMutate && (
            <button
              type="button"
              onClick={openCreateModal}
              className="relative group w-full rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 sm:w-auto"
            >
              <span className="flex items-center gap-2">
                + New Quotation
                <span className="w-5 h-5 rounded-full border border-white/30 text-white text-xs flex items-center justify-center font-bold group-hover:bg-white/20 transition">?</span>
              </span>
              <span className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                Info: incoming only
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Filter Section */}
      <CollapsibleFilters>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Project</label>
            <MultiSelectFilterDropdown
              options={getUniqueProjectOptions()}
              selectedValues={filters.project_ids}
              onChange={(project_ids) => setFilters((prev) => ({ ...prev, project_ids }))}
              placeholder="All projects"
              emptyText="No projects"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
            <MultiSelectFilterDropdown
              options={getUniqueStatuses()}
              selectedValues={filters.statuses}
              onChange={(statuses) => setFilters((prev) => ({ ...prev, statuses }))}
              placeholder="All status"
              emptyText="No status options"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
            <MultiSelectFilterDropdown
              options={getUniqueClientNames()}
              selectedValues={filters.clientNames}
              onChange={(clientNames) => setFilters((prev) => ({ ...prev, clientNames }))}
              placeholder="All clients"
              emptyText="No quotation records"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Quotation No.</label>
            <MultiSelectFilterDropdown
              options={getUniqueQuotationNumbers()}
              selectedValues={filters.quotationNumbers}
              onChange={(quotationNumbers) => setFilters((prev) => ({ ...prev, quotationNumbers }))}
              placeholder="All quotation numbers"
              emptyText="No quotation numbers"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={resetFilters}
              className="btn-reset-standard"
            >
              🔄 Reset Filters
            </button>
          </div>
        </div>
      </CollapsibleFilters>

      <TableStateWrapper
        loading={loading}
        error={tableError}
        onRetry={fetchQuotations}
        loadingText="Loading quotation records..."
        containerRef={quotationTableRef}
      >
        <table className="w-full">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Quotation No.</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Quotation Type</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Status</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Project No.</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Project Name</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Client Name</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Recipient Name</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Amount</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Remarks</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Payment Term</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Issued Date</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Document</th>
              {!isViewer && <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Action</th>}
            </tr>
          </thead>
          <tbody>
            {filteredQuotations.length === 0 ? (
              <tr>
                <td colSpan={isViewer ? '11' : '12'} className="px-3 py-2 text-center text-gray-500">
                  No quotations yet. Create your first quotation!
                </td>
              </tr>
            ) : (
              paginatedQuotations.map((quotation) => {
                // Use project data from the quotation response (already included by API)
                const projectData = quotation.Project || {}
                const quotationType = getTypeBadge(quotation.remarks_type || quotation.remarksType || quotation.qt_type || quotation.qtType || quotation.quotation_type)
                const pdfGenerationStatus = String(quotation.pdf_generation_status || 'ready').toLowerCase()
                return (
                  <tr key={quotation.quotation_id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => handleViewDetails(quotation)}>
                    <td className="px-3 py-2 text-sm font-semibold text-blue-600 whitespace-nowrap text-left">{quotation.quotation_number}</td>
                    <td className="px-3 py-2 text-sm text-left whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${quotationType.className}`}>
                        {quotationType.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                        quotation.status === 'Proposal' ? 'bg-purple-100 text-purple-800' :
                        quotation.status === 'Confirm' ? 'bg-blue-100 text-blue-800' :
                        quotation.status === 'Close' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {quotation.status || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap text-left">{projectData?.project_code || 'N/A'}</td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap text-left"><TruncatedText text={projectData?.project_name} fallback="N/A" /></td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap text-left"><TruncatedText text={quotation.company_name} fallback="N/A" /></td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap text-left"><TruncatedText text={quotation.recipient_name} /></td>
                    <td className="px-3 py-2 text-sm font-medium text-center whitespace-nowrap">{formatCurrency(quotation.amount)}</td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap"><TruncatedText text={quotation.remarks} /></td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{quotation.payment_term || '-'}</td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{formatDate(quotation.quotation_date)}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      {quotationType.label === 'Outgoing' && pdfGenerationStatus === 'failed' ? (
                        <span
                          className="text-red-600 text-lg"
                          title={quotation.pdf_generation_error || 'PDF generation failed'}
                        >
                          ❌
                        </span>
                      ) : quotationType.label === 'Outgoing' && pdfGenerationStatus === 'pending' ? (
                        <span className="text-amber-600 text-lg" title="PDF generation in progress">⏳</span>
                      ) : quotationType.label === 'Outgoing' && pdfGenerationStatus === 'ready' && quotation.pdf_path ? (
                        <FileIconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            openQuotationTemplatePreview(quotation)
                          }}
                          icon="✨"
                          tooltip="PDF Ready. Open Preview"
                        />
                      ) : quotationType.label === 'Incoming' && (quotation.po_attachment || quotation.document_path || quotation.uploaded_file_path) ? (
                        <FileIconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            openUploadedFileViewer(quotation)
                          }}
                          icon="📎"
                          tooltip="View Uploaded File"
                        />
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    {!isViewer && (
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleViewDetails(quotation)
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteQuotation(quotation.quotation_id)
                          }}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
        <TablePagination
          currentPage={quotationPage}
          totalPages={totalQuotationPages}
          totalItems={filteredQuotations.length}
          rowsPerPage={quotationRowsPerPage}
          onPrevious={() => setQuotationPage((prev) => Math.max(prev - 1, 1))}
          onNext={() => setQuotationPage((prev) => Math.min(prev + 1, totalQuotationPages))}
        />
      </TableStateWrapper>

      {/* Create Quotation Modal */}
      {showModal && !showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className="modal-panel-standard bg-white max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold">Create New Quotation</h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <div className="mb-6 p-4 rounded-lg border bg-amber-50 border-amber-200 text-amber-800">
              <p className="font-medium">Incoming quotation mode. Add detail items as needed before creating the record.</p>
            </div>

            <div className="modal-form-grid-standard">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project <span className="text-red-600">*</span></label>
                <select
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="">-- Select Project --</option>
                  {projects.map(project => (
                    <option key={project.project_id} value={project.project_id}>
                      {project.project_code} - {project.project_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date <span className="text-red-600">*</span></label>
                <input
                  type="date"
                  name="quotation_date"
                  value={formData.quotation_date}
                  onChange={handleInputChange}
                  className="modal-date-input px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
                <input
                  type="text"
                  value={selectedProject?.company_name || ''}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Name</label>
                <input
                  type="text"
                  name="recipient_name"
                  value={formData.recipient_name}
                  onChange={handleInputChange}
                  placeholder="Person in charge"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount (RM)</label>
                <input
                  type="number"
                  value={items.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated from Detail Items total</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <input
                  type="text"
                  value="Proposal"
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quotation Type</label>
                <select
                  name="remarks_type"
                  value={formData.remarks_type}
                  onChange={handleInputChange}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                >
                  <option value="Incoming">Incoming</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
              <input
                type="text"
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                placeholder="Additional remarks..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Term</label>
              <input
                type="text"
                name="payment_term"
                value={formData.payment_term}
                onChange={handleInputChange}
                placeholder="e.g. Net 30, COD, Due on Completion"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Attachment (PDF/Image/DOC)</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null
                  setFormData((prev) => ({
                    ...prev,
                    po_attachment: file,
                    po_attachment_name: file ? file.name : ''
                  }))
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
              {formData.po_attachment_name && (
                <p className="text-xs text-gray-600 mt-1">Selected: {formData.po_attachment_name}</p>
              )}
            </div>

            <div className="border-t pt-4 mt-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Detail Items</h3>
              <div ref={quotationItemsTableRef} className="overflow-x-auto overflow-y-auto max-h-[70vh] border border-gray-300 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b border-gray-300">
                    <tr>
                      <th className="px-2 py-1 text-center font-semibold text-gray-700 w-8 whitespace-nowrap">#</th>
                      <th className="px-2 py-1 text-left font-semibold text-gray-700 whitespace-nowrap">Description</th>
                      <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Price</th>
                      <th className="px-2 py-1 text-center font-semibold text-gray-700 w-20 whitespace-nowrap">Qty</th>
                      <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Amount</th>
                      {!isViewer && <th className="px-2 py-1 text-center font-semibold text-gray-700 w-24 whitespace-nowrap">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedQuotationItems.map((item, idx) => (
                      <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-gray-50 whitespace-nowrap">{(quotationItemsPage - 1) * quotationItemsRowsPerPage + idx + 1}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{item.description}</td>
                        <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(item.price)}</td>
                        <td className="px-2 py-1 text-center whitespace-nowrap">{item.quantity.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(item.amount)}</td>
                        {!isViewer && (
                          <td className="px-2 py-1 text-center space-x-2">
                            <button
                              type="button"
                              onClick={() => startEditItem(item)}
                              title="Edit item"
                              className="inline-flex items-center justify-center w-8 h-8 text-blue-500 hover:text-blue-600 transition"
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              onClick={() => removeItem(item.id)}
                              className="inline-flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-600 transition"
                              title="Delete item"
                            >
                              ❌
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {editingItemId === null && !isViewer && (
                      <tr className="border-b border-gray-200 bg-blue-50">
                        <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-blue-100 whitespace-nowrap">{items.length + 1}</td>
                        <td className="px-2 py-1">
                          <textarea
                            name="description"
                            value={currentItem.description}
                            onChange={handleItemChange}
                            placeholder="Item description"
                            rows="2"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            name="price"
                            value={currentItem.price}
                            onChange={handleItemChange}
                            placeholder="0.00"
                            step="0.01"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            name="quantity"
                            value={currentItem.quantity}
                            onChange={handleItemChange}
                            placeholder="0"
                            step="0.01"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                          />
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                          {currentItem.price && currentItem.quantity
                            ? formatCurrency(parseFloat(currentItem.price) * parseFloat(currentItem.quantity))
                            : formatCurrency(0)}
                        </td>
                        <td className="px-2 py-1 text-center">
                          <button
                            type="button"
                            onClick={addItem}
                            title="Add new item"
                            className="inline-flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-700 transition"
                          >
                            ➕
                          </button>
                        </td>
                      </tr>
                    )}
                    {editingItemId !== null && (
                      <tr className="border-b border-gray-200 bg-green-50">
                        <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-green-100 whitespace-nowrap">{items.findIndex((item) => item.id === editingItemId) + 1}</td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            name="description"
                            value={currentItem.description}
                            onChange={handleItemChange}
                            placeholder="Item description"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            name="price"
                            value={currentItem.price}
                            onChange={handleItemChange}
                            placeholder="0.00"
                            step="0.01"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            name="quantity"
                            value={currentItem.quantity}
                            onChange={handleItemChange}
                            placeholder="0"
                            step="0.01"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                          />
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                          {currentItem.price && currentItem.quantity
                            ? formatCurrency(parseFloat(currentItem.price) * parseFloat(currentItem.quantity))
                            : formatCurrency(0)}
                        </td>
                        <td className="px-2 py-1 text-center space-x-2">
                          <button
                            type="button"
                            onClick={() => saveEditItem(editingItemId)}
                            title="Save changes"
                            className="inline-flex items-center justify-center w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded transition"
                          >
                            ✓
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingItemId(null)
                              setCurrentItem({ description: '', price: '', quantity: '' })
                            }}
                            title="Cancel editing"
                            className="inline-flex items-center justify-center w-8 h-8 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    )}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={isViewer ? '5' : '6'} className="px-3 py-2 text-center text-gray-500">No detail items yet.</td>
                      </tr>
                    )}
                    {items.length > 0 && (
                      <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                        <td colSpan="4" className="px-2 py-1 text-right whitespace-nowrap">Total:</td>
                        <td className="px-2 py-1 text-right text-blue-700 whitespace-nowrap">{formatCurrency(getTotalAmount())}</td>
                        {!isViewer && <td></td>}
                      </tr>
                    )}
                  </tbody>
                </table>
                <TablePagination
                  currentPage={quotationItemsPage}
                  totalPages={totalQuotationItemPages}
                  totalItems={items.length}
                  rowsPerPage={quotationItemsRowsPerPage}
                  onPrevious={() => setQuotationItemsPage((prev) => Math.max(prev - 1, 1))}
                  onNext={() => setQuotationItemsPage((prev) => Math.min(prev + 1, totalQuotationItemPages))}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-6 border-t mt-6">
              <button
                onClick={handleCreateQuotation}
                disabled={loading || !canMutate || !formData.project_id || !formData.quotation_date || items.length === 0}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Quotation'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quotation Details Modal */}
      {showDetailsModal && selectedQuotation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => closeDetailsModal()}>
          <div className="modal-panel-standard bg-white max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold">Edit Quotation No. {selectedQuotation.quotation_number}</h2>
              <button
                type="button"
                onClick={closeDetailsModal}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            
            {/* Status Info Banner */}
            {!isFieldsEditable && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 font-medium">
                  ⚠️ This quotation is in <strong>{formData.status}</strong> status. All fields are read-only and cannot be edited.
                </p>
              </div>
            )}
            
            {isFieldsEditable && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 font-medium">
                  ✓ This quotation is in <strong>Proposal</strong> status. You can edit the fields below.
                </p>
              </div>
            )}
            
            <div className="modal-form-grid-standard">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quotation No.</label>
                <input
                  type="text"
                  value={formData.quotation_number}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                <input
                  type="text"
                  value={selectedQuotation?.Project?.project_name || 'N/A'}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
                <input
                  type="text"
                  value={selectedQuotation.company_name || 'N/A'}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Name</label>
                <input
                  type="text"
                  name="recipient_name"
                  value={formData.recipient_name}
                  onChange={handleInputChange}
                  disabled={!isFieldsEditable}
                  placeholder="Person in charge"
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                    isFieldsEditable
                      ? 'focus:ring-2 focus:ring-blue-600 focus:border-transparent'
                      : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount (RM) <span className="text-red-600">*</span></label>
                <input
                  type="number"
                  name="quotation_amount"
                  value={formData.quotation_amount}
                  disabled
                  step="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated from Detail Items total</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date <span className="text-red-600">*</span></label>
                <input
                  type="date"
                  name="quotation_date"
                  value={formData.quotation_date}
                  onChange={handleInputChange}
                  disabled={!isFieldsEditable}
                  className={`modal-date-input px-4 py-2 border border-gray-300 rounded-lg ${
                    isFieldsEditable 
                      ? 'focus:ring-2 focus:ring-blue-600 focus:border-transparent' 
                      : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={!isFieldsEditable}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                    isFieldsEditable 
                      ? 'focus:ring-2 focus:ring-blue-600 focus:border-transparent' 
                      : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  <option value="Proposal">Proposal</option>
                  <option value="Confirm">Confirm</option>
                  <option value="Close">Close</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
              <input
                type="text"
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                disabled={!isFieldsEditable}
                placeholder="Additional remarks..."
                rows="3"
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                  isFieldsEditable 
                    ? 'focus:ring-2 focus:ring-blue-600 focus:border-transparent' 
                    : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                }`}
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Term</label>
              <input
                type="text"
                name="payment_term"
                value={formData.payment_term}
                onChange={handleInputChange}
                disabled={!isFieldsEditable}
                placeholder="e.g. Net 30, COD, Due on Completion"
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                  isFieldsEditable
                    ? 'focus:ring-2 focus:ring-blue-600 focus:border-transparent'
                    : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                }`}
              />
            </div>

            <div className="mt-6"> {/*  grid grid-cols-2 gap-6"> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quotation Type</label>
                <select
                  name="remarks_type"
                  value={formData.remarks_type}
                  onChange={handleInputChange}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                >
                  <option value="">Select...</option>
                  <option value="Outgoing">Outgoing</option>
                  <option value="Incoming">Incoming</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PO No. {formData.status === 'Confirm' && <span className="text-red-600">*</span>}</label>
                <input
                  type="text"
                  name="po_no"
                  value={formData.po_no}
                  onChange={handleInputChange}
                  disabled={!isFieldsEditable}
                  placeholder="eg. PO-2024-001"
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                    isFieldsEditable
                      ? 'focus:ring-2 focus:ring-blue-600 focus:border-transparent'
                      : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                  }`}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">PO Attachment {formData.status === 'Confirm' && <span className="text-red-600">*</span>}</label>
              {formData.po_attachment_name && <p className="text-xs text-green-700 mb-1">Current: {formData.po_attachment_name}</p>}
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null
                  setFormData((prev) => ({
                    ...prev,
                    po_attachment: file,
                    po_attachment_name: file ? file.name : prev.po_attachment_name
                  }))
                }}
                disabled={!isFieldsEditable}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                  isFieldsEditable
                    ? 'focus:ring-2 focus:ring-blue-600 focus:border-transparent'
                    : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                }`}
              />
            </div>

            {/* Detail Items Section */}
            <div className="border-t pt-4 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Detail Items</h3>
              </div>
              
              {items.length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    ℹ️ Edit items here or in the Projects page. Changes to detail items will be saved to the project.
                  </p>
                </div>
              )}
              
              {/* Detail Items Table */}
              <div ref={quotationItemsTableRef} className="overflow-x-auto overflow-y-auto max-h-[70vh] border border-gray-300 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b border-gray-300">
                    <tr>
                      <th className="px-2 py-1 text-center font-semibold text-gray-700 w-8 whitespace-nowrap">#</th>
                      <th className="px-2 py-1 text-left font-semibold text-gray-700 whitespace-nowrap">Description</th>
                      <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Price</th>
                      <th className="px-2 py-1 text-center font-semibold text-gray-700 w-20 whitespace-nowrap">Qty</th>
                      <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Amount</th>
                      {!isViewer && <th className="px-2 py-1 text-center font-semibold text-gray-700 w-24 whitespace-nowrap">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedQuotationItems.map((item, idx) => (
                      <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-gray-50 whitespace-nowrap">{(quotationItemsPage - 1) * quotationItemsRowsPerPage + idx + 1}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{item.description}</td>
                        <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(item.price)}</td>
                        <td className="px-2 py-1 text-center whitespace-nowrap">{item.quantity.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(item.amount)}</td>
                        {!isViewer && (
                          <td className="px-2 py-1 text-center space-x-2">
                            <button
                              type="button"
                              onClick={() => startEditItem(item)}
                              disabled={!isFieldsEditable || formData.status === 'Close'}
                              title="Delete item"
                              className="inline-flex items-center justify-center w-8 h-8 text-blue-500 hover:text-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              onClick={() => removeItem(item.id)}
                              disabled={!isFieldsEditable || formData.status === 'Close'}
                              title={isFieldsEditable ? "Delete item" : "Cannot delete (Confirm status)"}
                              className="inline-flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              ❌
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {editingItemId === null && isFieldsEditable && (
                      <tr className="border-b border-gray-200 bg-blue-50">
                        <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-blue-100 whitespace-nowrap">{items.length + 1}</td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            name="description"
                            value={currentItem.description}
                            onChange={handleItemChange}
                            placeholder="Item description"
                            disabled={!isFieldsEditable}
                            className={`w-full px-2 py-1 border border-gray-300 rounded text-xs ${
                              isFieldsEditable 
                                ? '' 
                                : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                            }`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            name="price"
                            value={currentItem.price}
                            onChange={handleItemChange}
                            placeholder="0.00"
                            step="0.01"
                            disabled={!isFieldsEditable}
                            className={`w-full px-2 py-1 border border-gray-300 rounded text-xs text-right ${
                              isFieldsEditable 
                                ? '' 
                                : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                            }`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            name="quantity"
                            value={currentItem.quantity}
                            onChange={handleItemChange}
                            placeholder="0"
                            step="0.01"
                            disabled={!isFieldsEditable}
                            className={`w-full px-2 py-1 border border-gray-300 rounded text-xs text-center ${
                              isFieldsEditable 
                                ? '' 
                                : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                            }`}
                          />
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                          {currentItem.price && currentItem.quantity
                            ? formatCurrency(parseFloat(currentItem.price) * parseFloat(currentItem.quantity))
                            : formatCurrency(0)}
                        </td>
                        <td className="px-2 py-1 text-center">
                          <button
                            type="button"
                            onClick={addItem}
                            disabled={!isFieldsEditable}
                            title={isFieldsEditable ? "Add new item" : "Cannot add (Confirm status)"}
                            className={`inline-flex items-center justify-center w-8 h-8 transition ${
                              isFieldsEditable 
                                ? 'text-green-600 hover:text-green-700 cursor-pointer' 
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            ➕
                          </button>
                        </td>
                      </tr>
                    )}
                    {editingItemId !== null && isFieldsEditable && (
                      <tr className="border-b border-gray-200 bg-green-50">
                        <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-green-100 whitespace-nowrap">{items.findIndex(item => item.id === editingItemId) + 1}</td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            name="description"
                            value={currentItem.description}
                            onChange={handleItemChange}
                            placeholder="Item description"
                            disabled={!isFieldsEditable}
                            className={`w-full px-2 py-1 border border-gray-300 rounded text-xs ${
                              isFieldsEditable 
                                ? '' 
                                : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                            }`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            name="price"
                            value={currentItem.price}
                            onChange={handleItemChange}
                            placeholder="0.00"
                            step="0.01"
                            disabled={!isFieldsEditable}
                            className={`w-full px-2 py-1 border border-gray-300 rounded text-xs text-right ${
                              isFieldsEditable 
                                ? '' 
                                : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                            }`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            name="quantity"
                            value={currentItem.quantity}
                            onChange={handleItemChange}
                            placeholder="0"
                            step="0.01"
                            disabled={!isFieldsEditable}
                            className={`w-full px-2 py-1 border border-gray-300 rounded text-xs text-center ${
                              isFieldsEditable 
                                ? '' 
                                : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                            }`}
                          />
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                          {currentItem.price && currentItem.quantity
                            ? formatCurrency(parseFloat(currentItem.price) * parseFloat(currentItem.quantity))
                            : formatCurrency(0)}
                        </td>
                        <td className="px-2 py-1 text-center space-x-2">
                          <button
                            type="button"
                            onClick={() => saveEditItem(editingItemId)}
                            disabled={!isFieldsEditable}
                            title={isFieldsEditable ? "Save changes" : "Cannot save (Confirm status)"}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded transition text-sm text-white ${
                              isFieldsEditable 
                                ? 'bg-green-600 hover:bg-green-700 cursor-pointer' 
                                : 'bg-gray-400 cursor-not-allowed'
                            }`}
                          >
                            ✓
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingItemId(null)
                              setCurrentItem({ description: '', price: '', quantity: '' })
                            }}
                            disabled={!isFieldsEditable}
                            title={isFieldsEditable ? "Cancel edit" : "Cannot cancel (Confirm status)"}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded transition text-sm text-white ${
                              isFieldsEditable 
                                ? 'bg-gray-600 hover:bg-gray-700 cursor-pointer' 
                                : 'bg-gray-400 cursor-not-allowed'
                            }`}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    )}
                    {items.length > 0 && (
                      <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                        <td colSpan="4" className="px-2 py-1 text-right whitespace-nowrap">Total:</td>
                        <td className="px-2 py-1 text-right text-blue-700 whitespace-nowrap">{formatCurrency(getTotalAmount())}</td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <TablePagination
                  currentPage={quotationItemsPage}
                  totalPages={totalQuotationItemPages}
                  totalItems={items.length}
                  rowsPerPage={quotationItemsRowsPerPage}
                  onPrevious={() => setQuotationItemsPage((prev) => Math.max(prev - 1, 1))}
                  onNext={() => setQuotationItemsPage((prev) => Math.min(prev + 1, totalQuotationItemPages))}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t mt-6">
              <button
                onClick={handleUpdateQuotation}
                disabled={loading || !canMutate || !formData.quotation_amount || !formData.quotation_date || !isFieldsEditable || isConfirmWithoutAttachment}
                title={
                  !canMutate
                    ? 'View-only role cannot update quotations'
                    : !isFieldsEditable
                    ? 'Cannot update (quotation is not in Proposal status)'
                    : isConfirmWithoutAttachment
                      ? 'Upload PO attachment before updating as Confirm'
                      : 'Update quotation'
                }
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      <GeneratedPdfPreviewModal
        isOpen={Boolean(viewingPdfPath)}
        pdfUrl={viewingPdfPath}
        title="Quotation PDF"
        onClose={() => setViewingPdfPath(null)}
        onDownload={handleDownloadPreviewPdf}
      />

      <TemplateDocumentPreviewModal
        isOpen={Boolean(templatePreviewData)}
        template={templatePreviewData?.template}
        title={templatePreviewData?.title || 'Quotation Preview'}
        draft={templatePreviewData?.draft}
        items={templatePreviewData?.items}
        totals={templatePreviewData?.totals}
        onDownload={templatePreviewData ? handleDownloadTemplatePreviewPdf : undefined}
        onClose={() => setTemplatePreviewData(null)}
      />

      {viewingFileInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={closeFileViewer}>
          <div className="bg-white rounded-lg w-full h-full max-w-4xl max-h-screen overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">File Viewer</h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (viewerDownloadFile?.path) {
                      downloadFile(viewerDownloadFile.path, viewerDownloadFile.name)
                    }
                  }}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition"
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={closeFileViewer}
                  className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 text-gray-800 text-sm rounded transition"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {['pdf'].includes(getFileExtension(viewingFileInModal)) ? (
                <iframe
                  src={viewingFileInModal}
                  className="w-full h-full border-none"
                  title="File Viewer"
                />
              ) : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(getFileExtension(viewingFileInModal)) ? (
                <img
                  src={viewingFileInModal}
                  alt="File"
                  className="max-w-none"
                />
              ) : (
                <div className="p-6 text-gray-600">
                  <p className="text-lg mb-2">Preview not available for this file type</p>
                  <p className="text-sm text-gray-500">Please download the file to view it.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {messageModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => setMessageModal({ open: false, title: 'Notification', message: '' })}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-800">{messageModal.title}</h3>
              <button
                type="button"
                onClick={() => setMessageModal({ open: false, title: 'Notification', message: '' })}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <p className="text-gray-700 whitespace-pre-line">{messageModal.message}</p>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setMessageModal({ open: false, title: 'Notification', message: '' })}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => {
          confirmModal.resolver && confirmModal.resolver(false)
          setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
        }}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-800">{confirmModal.title}</h3>
              <button
                type="button"
                onClick={() => {
                  confirmModal.resolver && confirmModal.resolver(false)
                  setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
                }}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <p className="text-gray-700 whitespace-pre-line">{confirmModal.message}</p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => {
                  confirmModal.resolver && confirmModal.resolver(false)
                  setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
                }}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmModal.resolver && confirmModal.resolver(true)
                  setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
